import { AccountingStatus } from "@/types";
import { isCountedAssignment } from "@/utils/stadiumAuditRowStatus";

// Map raw mission assignment statuses (legacy values) to unified accounting statuses.
const ASSIGNMENT_STATUS_MAP: Record<string, AccountingStatus> = {
    draft: AccountingStatus.NOT_ENTERED,
    not_entered: AccountingStatus.NOT_ENTERED,
    pending: AccountingStatus.PENDING_VALIDATION,
    pending_validation: AccountingStatus.PENDING_VALIDATION,
    completed: AccountingStatus.PENDING_VALIDATION,
    validated: AccountingStatus.VALIDATED,
    paid: AccountingStatus.CLOSED,
    closed: AccountingStatus.CLOSED,
    rejected: AccountingStatus.REJECTED,
};

// Map mission level workflow statuses to accounting equivalents.
const MISSION_STATUS_MAP: Record<string, AccountingStatus> = {
    scheduled: AccountingStatus.NOT_ENTERED,
    pending: AccountingStatus.NOT_ENTERED,
    in_progress: AccountingStatus.NOT_ENTERED,
    draft: AccountingStatus.NOT_ENTERED,
    completed: AccountingStatus.PENDING_VALIDATION,
    pending_validation: AccountingStatus.PENDING_VALIDATION,
    validated: AccountingStatus.VALIDATED,
    rejected: AccountingStatus.REJECTED,
    cancelled: AccountingStatus.REJECTED,
    closed: AccountingStatus.CLOSED,
    archived: AccountingStatus.CLOSED,
};

type AssignmentLike = {
    /** mission_assignment_status — `cancelled` = affectation retirée, conservée pour la trace. */
    status?: string | null;
    accounting_status?: string | null;
};

type MissionLike = {
    mission_status?: string | null;
    accountingStatus?: AccountingStatus | null;
    mission_assignments?: Array<AssignmentLike> | null;
};

/** Affectations qui comptent en comptabilité.
 *
 *  Une affectation `cancelled` (officiel retiré d'un audit déjà validé par
 *  audit.correct_team) reste en base pour la trace, mais son `accounting_status` est figé
 *  à la valeur qu'il avait au retrait. Sans ce filtre elle empêche à jamais un audit
 *  entièrement payé d'atteindre `closed` (il reste bloqué en « Validé »), elle double-compte
 *  dans les totaux et elle réapparaît dans la liste d'officiels de la carte kanban. */
export const countedAssignments = <T extends AssignmentLike>(
    assignments: Array<T> | null | undefined
): T[] => (assignments ?? []).filter(isCountedAssignment);

export const normalizeMissionAssignmentAccountingStatus = (
    status: string | null | undefined
): AccountingStatus => {
    if (!status) {
        return AccountingStatus.NOT_ENTERED;
    }
    const key = status.toLowerCase();
    return ASSIGNMENT_STATUS_MAP[key] ?? AccountingStatus.NOT_ENTERED;
};

export const normalizeMissionStatusToAccounting = (
    missionStatus: string | null | undefined
): AccountingStatus => {
    if (!missionStatus) {
        return AccountingStatus.NOT_ENTERED;
    }
    const key = missionStatus.toLowerCase();
    return MISSION_STATUS_MAP[key] ?? AccountingStatus.NOT_ENTERED;
};

export const resolveMissionAccountingStatus = (
    mission: MissionLike
): AccountingStatus => {
    // Explicit accounting status wins
    if (mission.accountingStatus) {
        return mission.accountingStatus;
    }

    const missionStatusAccounting = normalizeMissionStatusToAccounting(
        mission.mission_status
    );

    // If the mission workflow says “not entered”, prefer that over stale assignment states
    if (missionStatusAccounting === AccountingStatus.NOT_ENTERED) {
        return AccountingStatus.NOT_ENTERED;
    }

    const assignments = countedAssignments(mission.mission_assignments);

    if (assignments.length > 0) {
        const resolvedStatuses = assignments.map((assignment) =>
            normalizeMissionAssignmentAccountingStatus(assignment.accounting_status)
        );

        // If all assignments are back to NOT_ENTERED, treat the mission as NOT_ENTERED
        if (
            resolvedStatuses.length > 0 &&
            resolvedStatuses.every((status) => status === AccountingStatus.NOT_ENTERED)
        ) {
            return AccountingStatus.NOT_ENTERED;
        }

        if (resolvedStatuses.some((status) => status === AccountingStatus.REJECTED)) {
            return AccountingStatus.REJECTED;
        }

        if (resolvedStatuses.some((status) => status === AccountingStatus.PENDING_VALIDATION)) {
            return AccountingStatus.PENDING_VALIDATION;
        }

        if (
            resolvedStatuses.length > 0 &&
            resolvedStatuses.every((status) => status === AccountingStatus.VALIDATED)
        ) {
            return AccountingStatus.VALIDATED;
        }

        if (
            resolvedStatuses.length > 0 &&
            resolvedStatuses.every((status) => status === AccountingStatus.CLOSED)
        ) {
            return AccountingStatus.CLOSED;
        }
    }

    return missionStatusAccounting;
};

export const hasMissionAssignmentsPendingValidation = (
    rawAssignments: Array<AssignmentLike> | null | undefined
): boolean => {
    const assignments = countedAssignments(rawAssignments);
    if (assignments.length === 0) {
        return false;
    }

    return assignments.some((assignment) => {
        const status = normalizeMissionAssignmentAccountingStatus(
            assignment.accounting_status
        );
        return status === AccountingStatus.PENDING_VALIDATION;
    });
};

export const areAllMissionAssignmentsValidated = (
    rawAssignments: Array<AssignmentLike> | null | undefined
): boolean => {
    const assignments = countedAssignments(rawAssignments);
    if (assignments.length === 0) {
        return false;
    }

    return assignments.every((assignment) => {
        const status = normalizeMissionAssignmentAccountingStatus(
            assignment.accounting_status
        );
        return status === AccountingStatus.VALIDATED;
    });
};

/** Frontière unique de la comptabilité des missions : tout ce qui alimente le kanban
 *  (audits stade et missions manuelles) passe ici, dans AccountingContainer.
 *
 *  C'est donc ici, et nulle part en aval, que les affectations annulées sortent : la carte
 *  kanban, la recherche par nom d'officiel, les totaux d'indemnités, les compteurs
 *  « absent / non affecté » et le statut comptable de la mission lisent tous le tableau
 *  renvoyé par cette fonction. Un officiel retiré d'un audit validé ne doit plus y figurer.
 */
export const normalizeMissionForAccounting = (
    mission: any,
    overrides?: Record<string, unknown>
) => {
    const rawAssignments = Array.isArray(mission.mission_assignments)
        ? mission.mission_assignments
        : [];
    const assignments = countedAssignments(rawAssignments).map((assignment: any) => {
        const indemnity =
            assignment.indemnityAmount ?? assignment.payment_amount ?? 0;
        const travelDistance =
            assignment.travelDistanceInKm ?? assignment.travel_distance_km ?? 0;
        const normalizedStatus = normalizeMissionAssignmentAccountingStatus(
            assignment.accounting_status
        );

        return {
            ...assignment,
            raw_accounting_status: assignment.accounting_status ?? null,
            accounting_status: normalizedStatus,
            indemnityAmount: Number(indemnity) || 0,
            travelDistanceInKm: Number(travelDistance) || 0,
            isAbsent: assignment.isAbsent ?? assignment.is_absent ?? false,
            isNotAssigned: !assignment.official_id,
        };
    });

    const accountingStatus = resolveMissionAccountingStatus({
        mission_status: mission.mission_status,
        accountingStatus: mission.accountingStatus,
        mission_assignments: assignments,
    });

    return {
        ...mission,
        ...overrides,
        mission_assignments: assignments,
        accountingStatus,
        // Une mission qui AVAIT des affectations mais dont toutes sont `cancelled` est une
        // visite annulée (ou entièrement vidée par correction d'équipe) : il n'y a plus
        // rien à saisir ni à valider. Sans ce drapeau, resolveMissionAccountingStatus
        // retombe sur mission_status — et une mission restée `completed` sous un audit
        // annulé fabrique une carte « En Attente de Validation » sans aucun officiel.
        allAssignmentsCancelled:
            rawAssignments.length > 0 && assignments.length === 0,
    };
};

/** Filter out NOT_ENTERED missions that have no assignments and aren't scheduled/pending,
 *  and cancelled/emptied visits (every assignment cancelled) whatever their status. */
export const filterNormalizedMissions = (missions: any[]): any[] =>
    missions.filter((mission) => {
        if (mission.allAssignmentsCancelled) {
            return false;
        }
        if (mission.accountingStatus === AccountingStatus.NOT_ENTERED) {
            return (
                ["scheduled", "pending", null, undefined].includes(
                    mission.mission_status
                ) || mission.mission_assignments.length > 0
            );
        }
        return true;
    });
